import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../firebase/auth-context';

type LoginMode = 'password' | 'link' | 'complete';

/** Login page — Firebase Auth email/password or email link (admin accounts only). */
export default function Login() {
  const {
    signIn,
    sendSignInLink,
    completeSignInWithEmailLink,
    isEmailLinkRedirect,
    user,
    isAdmin,
    loading,
  } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<LoginMode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // If we arrived via an email link redirect, try to complete sign-in.
  useEffect(() => {
    if (!isEmailLinkRedirect || loading || user) return;

    // Retrieve the email from localStorage (stored when the link was sent).
    const storedEmail = window.localStorage.getItem('emailForSignIn');
    if (!storedEmail) {
      // Link opened on a different device/browser than the requester —
      // collect the email and complete sign-in against the current URL
      // (LA-827). Resending a link here would loop forever.
      setInfo(
        'This sign-in link was requested on a different device or browser. ' +
          'Enter the email address the link was sent to in order to complete sign-in.',
      );
      setMode('complete');
      return;
    }

    setSubmitting(true);
    setError(null);
    completeSignInWithEmailLink(storedEmail)
      .then(() => {
        navigate('/', { replace: true });
      })
      .catch((err) => {
        const message =
          err instanceof Error ? err.message : 'Email link sign-in failed';
        setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/.*\)\.?$/i, ''));
      })
      .finally(() => setSubmitting(false));
  }, [isEmailLinkRedirect, loading, user, completeSignInWithEmailLink, navigate]);

  // Already signed in as admin → redirect to overview.
  if (!loading && user && isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'password') {
        await signIn(email.trim(), password);
      } else if (mode === 'link') {
        await sendSignInLink(email.trim());
        setLinkSent(true);
      } else {
        // 'complete' — finish the email-link sign-in against the URL
        // that brought us here (window.location.href inside auth-context).
        await completeSignInWithEmailLink(email.trim());
      }
      // For password/complete modes, auth state update triggers redirect.
      // For link mode, we show a "check your email" message.
      if (mode !== 'link') {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Sign-in failed';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/.*\)\.?$/i, ''));
    } finally {
      setSubmitting(false);
    }
  };

  // If the email link was sent, show a confirmation message.
  if (linkSent) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Check your email</h1>
          <div className="auth-subtitle">
            A sign-in link has been sent to <strong>{email}</strong>
          </div>
          <p className="auth-info">
            Click the link in the email to sign in to the admin dashboard.
            The link will open this page and complete the sign-in automatically.
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setLinkSent(false);
              setMode('password');
              setPassword('');
            }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Lift Assisted</h1>
        <div className="auth-subtitle">Admin Dashboard — sign in</div>

        {mode !== 'complete' && (
          <div className="login-mode-toggle">
            <button
              type="button"
              className={mode === 'password' ? 'active' : ''}
              onClick={() => { setMode('password'); setError(null); }}
            >
              Password
            </button>
            <button
              type="button"
              className={mode === 'link' ? 'active' : ''}
              onClick={() => { setMode('link'); setError(null); }}
            >
              Email link
            </button>
          </div>
        )}

        {info && <p className="auth-info">{info}</p>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
              autoFocus
            />
          </div>
          {mode === 'password' && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
          )}
          {error && <div className="alert alert-error">{error}</div>}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !email || (mode === 'password' && !password)}
          >
            {submitting
              ? mode === 'link'
                ? 'Sending…'
                : 'Signing in…'
              : mode === 'password'
                ? 'Sign in'
                : mode === 'link'
                  ? 'Send sign-in link'
                  : 'Complete sign-in'}
          </button>
          {mode === 'complete' && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting}
              onClick={() => {
                // Escape route — the link may be expired or the email
                // mistyped; request a fresh link on this device instead.
                setMode('link');
                setInfo(null);
                setError(null);
              }}
            >
              Request a new sign-in link
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
